"use strict";
import { history } from "umi";
import { svc_addOne, svc_editOne, svc_search, svc_deleteOne } from "@/services";
import { userLogin, userLogout, getMe, changePassword } from "@/services/session";
import { svc_getMemo2, svc_saveMemo2 } from "@/services/preferences";
import { getPageQuery } from "@/utils/utils";
import { redirectUserToLoginPage } from "@/utils/redirect";
import { showServerResponse } from "@/utils/message.js";
import { getJWTToken, setJWTToken } from "@/utils/jwt";
import { convertISODateTimeToString } from "@/utils/convertTime";
import logger from "@/utils/logger";
import config from "../../config/config.json";

const UserModel = {
	namespace: "m_UserSession",
	state: {
		currentUser: {},
		hasJWT: false,
		status: undefined,
		apiKeys: {},
		memo2: undefined,
	},
	reducers: {
		updateCurrentUserState(state, { payload, jwtFromStorage }) {
			if (payload.id) {
				return { ...state, currentUser: payload, hasJWT: jwtFromStorage ? true : false };
			} else {
				return { ...state, hasJWT: jwtFromStorage ? true : false };
			}
		},
		changeLoginStatus(state, { payload }) {
			return { ...state, status: payload.status, type: payload.type };
		},
		changeNotifyCount(
			state = {
				currentUser: {},
			},
			action,
		) {
			return {
				...state,
				currentUser: {
					...state.currentUser,
					notifyCount: action.payload.totalCount,
					unreadCount: action.payload.unreadCount,
				},
			};
		},
		handleFetchAllApiKeys(state, { payload }) {
			return { ...state, apiKeys: payload };
		},
        saveStateMemo2(state, { payload }) {
			return { ...state, memo2: payload };
		},
	},
	effects: {
		*login({ payload }, { call, put }) {
			const response = yield call(userLogin, payload);
			yield put({
				type: "changeLoginStatus",
				payload: response,
			});

			if (response.status === "OK!") {
				// Fetch User Information and store the JWT Token + User Information into local storage
				// User Information consists of all the policies for the user
				//
				// Note: JWT Token must be saved first! Request will intercept the request and retrieve JWT Tokne from localstorage
				setJWTToken(response);
				const userInformation = yield call(getMe);
				localStorage.setItem("user-information", JSON.stringify(userInformation));

				// Redirect the user to the redirect URL.
				const urlParams = new URL(window.location.href);
				const params = getPageQuery();
				let { redirect } = params;

				if (redirect) {
					const redirectUrlParams = new URL(redirect);

					if (redirectUrlParams.origin === urlParams.origin) {
						redirect = redirect.substr(urlParams.origin.length);

						if (redirect.match(/^\/.*#/)) {
							redirect = redirect.substr(redirect.indexOf("#") + 1);
						}
					} else {
						window.location.href = "/";
						return;
					}
				}
				history.replace(redirect || "/");
				window.location.reload(true);
			} else {
				// status is not OK
				logger("Invalid login!", 0);
				showServerResponse(response);
			}
		},
		*logout({ payload }, { call, put }) {
			const { redirect } = getPageQuery(); // Note: There may be security issues, please note

			const response = yield call(userLogout);
			showServerResponse(response, true);

			// Remove Local Storage Information such as JWT Token and User Information
			localStorage.clear();
			redirectUserToLoginPage(redirect);
		},
		*getMe(_, { call, put }) {
			let jwtFromStorage = getJWTToken();

			// If Datastore exists, try to get the user ID.
			if (jwtFromStorage) {
				const response = yield call(getMe);
				if (response.serverResponse) {
					// request.js will have handled any token problems.
					// return now to avoid the put call to updateCurrentUserState, so that the state info don't get messed up.
					return;
				}

				// response.avatar = '/avatar.png';
				response.avatar = config.publicResourceBaseURL + config.defaultAvatar;
				yield put({
					type: "updateCurrentUserState",
					payload: response,
					jwtFromStorage,
				});
			}
		},
		*changePassword({ payload }, { call, put }) {
			let results = yield call(changePassword, payload);
			if (results.status == "OK") {
				return true;
			} else {
				return false;
			}
		},

		*fetchAllApiKeys({ payload }, { call, put }) {
			const apiKeys = yield call(svc_search, "api-key", payload);

			_.forEach(apiKeys.result, function (row) {
				row.key = row.id;
				row = convertISODateTimeToString(row, ["expiry_date", "created_at"], config.dateTimeFormat);

				if (row.status !== "VALID")
					row.hideDeleteBtn = true;
			});

			yield put({
				type: "handleFetchAllApiKeys",
				payload: apiKeys,
			});

			return apiKeys;
		},
		*createApiKey({ payload }, { call, put }) {
			const response = yield call(svc_addOne, "api-key", payload);
			showServerResponse(response, true);
			return response;
		},
		*deleteApiKey({ payload }, { call, put }) {
			payload = _.omit(payload, ["key"]);
			const response = yield call(svc_deleteOne, "api-key", payload, "id", payload.name);
			return showServerResponse(response);
		},

		*fetchMemo2({ }, { call, put }) {
			let userMemo2 = yield call(svc_getMemo2);

            // use an empty object to represent that this has been triggered 
            // and is not an actual null value for database
            let payload = {};
            if (userMemo2.result && userMemo2.result[0].memo2) payload = userMemo2.result[0].memo2;

            // save into global state
			yield put({
				type: "saveStateMemo2",
				payload,
			});
		},
        *saveMemo2({ payload }, { call, put }) {
			yield call(svc_saveMemo2, payload);
            yield put({
				type: "saveStateMemo2",
				payload,
			});
		},
	},
};
export default UserModel;
